import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import { Box, Typography } from '@mui/material';
import {
    DatePicker,
    Option,
    Radio,
    Select,
    TextArea,
    TextField,
} from 'components/ui/FormControls';
import { Snackbar } from 'components/ui/Feedback';
import React, { ChangeEvent, useEffect, useState } from 'react';
import gql from 'graphql-tag';

import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import {
    FormSnackBarType,
    generateSnackBarFormMessage,
    useConstrainTextArea,
    useFormControl,
    useFormGroup,
    useManageTemplateForm,
} from '../../../utils/form.utils';
import {
    requiredValidator,
    pastDateValidator,
} from '../../../utils/validator.utils';
import ManageTemplate, {
    ManageTemplateInstance,
} from '../../ui/ManageTemplate';
import ManageTemplateFormItem from '../../ui/ManageTemplateFormItem';
import {
    PromotionalContentNullable,
    PromotionalContent,
    PromotionPage,
} from '../../../shared/types';
import { sortArray } from '../../../utils/sorting.utils';
import ManageTemplateDialog from '../../ui/ManageTemplateDialog';
import { useRoutesHistory } from '../../providers/RoutesHistoryProvider';
import {
    MessageTypes,
    useNotification,
} from '../../providers/NotificationProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';

import { useParams, useHistory } from 'react-router-dom';
import { sanitizeRouteId } from '../../../utils/route-params.utils';

const returnListUrl = baseUrls.publisher.promotionalContent.list;

interface RequestVariables {
    name: string;
    description?: string;
    version: string | undefined;
    articleImage: string;
    sidebarImage: string;
    link: string;
}

enum DestinationLinkSelection {
    NotSelected,
    ExternalLink,
    CustomLandingPage,
}

const defaultId = '-';

export const queryPromotionalContentCache = gql`
    query PromotionalContentQuery {
        savedItem {
            _id
            articleImage
            description
            link
            name
            promotionPageId
            sidebarImage
            version
            newPageName
        }
    }
`;

const ADD_PROMOTIONAL_CONTENT = gql`
    mutation addPromotionalContent($item: PromotionalContentInputAdd) {
        addPromotionalContent(item: $item) {
            _id
        }
    }
`;

const EDIT_PROMOTIONAL_CONTENT = gql`
    mutation editPromotionalContent($item: PromotionalContentInputEdit) {
        editPromotionalContent(item: $item) {
            _id
        }
    }
`;

export const PROMOTIONAL_CONTENT = gql`
    query promotionalContent($id: ID) {
        document: findPromotionalContentById(id: $id) {
            _id
            name
            description
            version
            articleImage
            sidebarImage
            link
            promotionPageId
            expiryDate
        }
        allPages: promotionPage {
            _id
            name
            html
            pageSummary
            socialSharingImageUrl
            successTitle
            successMessage
            promotionalContent {
                _id
            }
        }
    }
`;

export const DELETE_PROMOTIONAL_CONTENT = gql`
    mutation deletePromotionalContent($id: ID!) {
        deletePromotionalContent(id: $id) {
            _id
        }
    }
`;

const DELETE_PROMOTIONAL_PAGE = gql`
    mutation deletePromotionPage($item: PromotionPageInputDelete) {
        deletePromotionPage(item: $item) {
            _id
        }
    }
`;

export const PromotionalContentAddEdit = (): JSX.Element => {
    const { _id: routeId } = useParams<{ _id?: string }>();
    const _id = sanitizeRouteId(routeId);
    const history = useHistory();
    const { showMessage, messageType, clearMessage, message } =
        useNotification();

    const apolloClient = useApolloClient();
    const [destinationLinkSelection, setDestinationLinkSelection] =
        useState<DestinationLinkSelection>(
            DestinationLinkSelection.NotSelected
        );
    const [
        showDestinationLinkValidationError,
        setShowDestinationLinkValidationError,
    ] = useState(false);
    const [deletePromotionalPage] = useMutation(DELETE_PROMOTIONAL_PAGE, {
        refetchQueries: [
            { query: PROMOTIONAL_CONTENT, variables: { id: _id } },
        ],
        awaitRefetchQueries: true,
    });

    const [confirmationDialogOpened, setConfirmationDialogOpened] =
        useState<boolean>(false);

    const routesHistory = useRoutesHistory();

    const getReqBody = (clearLink = true): RequestVariables => {
        const { value } = form;
        const { link, promotionPageId, expiryDate, ...formValue } = value;

        const externalLink =
            !clearLink ||
            destinationLinkSelection === DestinationLinkSelection.ExternalLink
                ? link
                : '';
        const customPromotionPageId =
            destinationLinkSelection ===
            DestinationLinkSelection.CustomLandingPage
                ? promotionPageId
                : null;

        return {
            ...formValue,
            link: externalLink,
            expiryDate: expiryDate ? expiryDate : null,
            promotionPageId: customPromotionPageId,
            version: editItem?.version || undefined,
        };
    };

    const cachedItem = readContentFromCache(_id ?? defaultId);

    let promotionsOptions: Option[] = [];

    let pages: PromotionPage[] = [];

    const { loading, data } = useQuery(PROMOTIONAL_CONTENT, {
        variables: { id: _id },
        fetchPolicy: 'network-only',
        onCompleted: ({ document }) => {
            pages = data?.allPages ?? [];

            if (!document) {
                nameFormControl?.setInitialValue(cachedItem?.name ?? '');
                descriptionFormControl?.setInitialValue(
                    cachedItem?.description ?? ''
                );
                articleImageFormControl?.setInitialValue(
                    cachedItem?.articleImage ?? ''
                );
                sidebarImageFormControl?.setInitialValue(
                    cachedItem?.sidebarImage ?? ''
                );
                destinationLinkFormControl?.setInitialValue(
                    cachedItem?.link ?? ''
                );
                expiryDateFormControl?.setInitialValue(null);

                if (isReturnFromPromotionPage()) {
                    setDestinationLinkSelection(
                        DestinationLinkSelection.CustomLandingPage
                    );
                }

                setPageValue(pages, null);

                return;
            }

            setDestinationLinkSelection(
                !document.promotionPageId && !isReturnFromPromotionPage()
                    ? DestinationLinkSelection.ExternalLink
                    : DestinationLinkSelection.CustomLandingPage
            );

            nameFormControl?.setInitialValue(cachedItem?.name ?? document.name);
            descriptionFormControl?.setInitialValue(
                cachedItem?.description ?? (document.description || '')
            );
            constrainTextArea?.setCurrentChars(
                document.description?.length || 0
            );
            articleImageFormControl?.setInitialValue(
                cachedItem?.articleImage ?? document.articleImage
            );
            sidebarImageFormControl?.setInitialValue(
                cachedItem?.sidebarImage ?? document.sidebarImage
            );
            destinationLinkFormControl?.setInitialValue(
                cachedItem?.link ?? document.link
            );
            expiryDateFormControl?.setInitialValue(
                document?.expiryDate || undefined
            );

            setPageValue(pages, document);
        },
    });

    const editItem: PromotionalContent = data?.document;

    const nameFormControl = useFormControl<string>('', [requiredValidator]);
    const descriptionFormControl = useFormControl<string>('', []);
    const articleImageFormControl = useFormControl<string>('', [
        requiredValidator,
    ]);
    const sidebarImageFormControl = useFormControl<string>('', [
        requiredValidator,
    ]);
    const destinationLinkFormControl = useFormControl<string>('', []);
    const promotionsFormControl = useFormControl<Option | undefined>(
        undefined,
        []
    );
    const expiryDateFormControl = useFormControl<Date | undefined>(undefined, [
        pastDateValidator,
    ]);
    const constrainTextArea = useConstrainTextArea(0, 250);

    const form = useFormGroup({
        name: nameFormControl,
        description: descriptionFormControl,
        articleImage: articleImageFormControl,
        sidebarImage: sidebarImageFormControl,
        link: destinationLinkFormControl,
        promotionPageId: promotionsFormControl,
        expiryDate: expiryDateFormControl,
    });

    useEffect(() => {
        if (
            destinationLinkSelection ===
            DestinationLinkSelection.CustomLandingPage
        ) {
            promotionsFormControl.setValidators([requiredValidator]);
            destinationLinkFormControl.setValidators([]);
        }

        if (
            destinationLinkSelection === DestinationLinkSelection.ExternalLink
        ) {
            destinationLinkFormControl.setValidators([requiredValidator]);
            promotionsFormControl.setValidators([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationLinkSelection]);

    const {
        isNew,
        formMessage,
        formBusy,
        onSubmit,
        onCancel,
        onDelete,
        onAlertBoxClose,
        updateFormMessage,
    } = useManageTemplateForm(
        ManageTemplateInstance.PromotionalContent,
        form,
        editItem,
        ADD_PROMOTIONAL_CONTENT,
        EDIT_PROMOTIONAL_CONTENT,
        DELETE_PROMOTIONAL_CONTENT,
        returnListUrl
    );

    function isReturnFromPromotionPage(): boolean {
        const visitStack = routesHistory?.history || [];
        if (visitStack.length < 2) return false;

        const previousURL: string = visitStack[visitStack.length - 2];
        if (!previousURL) return false;

        return (
            previousURL.includes('/promotional-page/add') ||
            previousURL.includes('/promotional-page/edit')
        );
    }

    function readContentFromCache(
        currentId: string
    ): PromotionalContentNullable {
        try {
            if (!isReturnFromPromotionPage()) {
                return null;
            }

            const readData = apolloClient.readQuery({
                query: queryPromotionalContentCache,
            });
            const resultItem: PromotionalContentNullable = readData?.savedItem;

            return resultItem && resultItem._id !== currentId
                ? null
                : resultItem;
        } catch (ex) {
            return null;
        }
    }

    function saveContentToCache(isEmpty: boolean) {
        const itemToSave: any = isEmpty ? null : getReqBody(false);

        if (itemToSave) {
            itemToSave['_id'] = editItem?._id ?? defaultId;
            if (!itemToSave.promotionPageId) {
                itemToSave['promotionPageId'] = defaultId;
            }

            itemToSave['newPageName'] = '';

            itemToSave['__typename'] = 'PromotionalContentCached';
            if (!itemToSave.version) {
                itemToSave['version'] = 1;
            }
        }

        apolloClient.writeQuery({
            query: queryPromotionalContentCache,
            data: {
                savedItem: isEmpty ? null : itemToSave,
            },
        });
    }

    function setPageValue(
        pages: PromotionPage[],
        editContent: PromotionalContent | null
    ) {
        promotionsOptions = sortArray(
            [
                ...pages.map(
                    (x) =>
                        ({
                            label: x.name,
                            value: x._id,
                        }) as Option
                ),
            ],
            'label'
        );

        const selectedPageValue = promotionsFormControl?.value
            ? promotionsFormControl.value.value
            : '-';

        if (cachedItem && cachedItem.newPageName) {
            // set correct promotional page after creating a new one
            const newPage = promotionsOptions.find(
                (po) => po.label === cachedItem.newPageName
            );
            if (newPage && newPage.value !== selectedPageValue) {
                promotionsFormControl?.setValue(newPage);
                saveContentToCache(true);
            }
        } else {
            // set correct promotional page otherwise
            if (shouldSetPageInitialValue()) {
                const pageId =
                    cachedItem?.promotionPageId ?? editContent?.promotionPageId;

                if (pageId && pageId !== selectedPageValue) {
                    promotionsFormControl?.setInitialValue(
                        promotionsOptions.find((po) => po.value === pageId)
                    );
                }
            }
        }
    }

    function shouldSetPageInitialValue() {
        if (!promotionsFormControl.isChanged) return true;

        return (
            formMessage &&
            formMessage.style === 'error' &&
            typeof formMessage.message === 'string' &&
            formMessage.message?.includes('delete promotion page')
        );
    }

    if (loading) return <Loader />;

    pages = data?.allPages ?? [];
    promotionsOptions = sortArray(
        [
            ...pages.map(
                (x) =>
                    ({
                        label: x.name,
                        value: x._id,
                    }) as Option
            ),
        ],
        'label'
    );

    const getDestinationFields = () => {
        if (
            destinationLinkSelection === DestinationLinkSelection.ExternalLink
        ) {
            return (
                <ManageTemplateFormItem>
                    <TextField
                        id={'destination-link-form-control'}
                        onChange={(
                            ev: ChangeEvent<
                                HTMLInputElement | HTMLTextAreaElement
                            >
                        ): void =>
                            destinationLinkFormControl?.setValue(
                                ev?.target?.value
                            )
                        }
                        onBlur={destinationLinkFormControl?.handleBlur}
                        label="External destination link"
                        helperText="This link will open in a new window when the user registers interest"
                        placeholder="Destination link URL"
                        autoComplete="off"
                        error={destinationLinkFormControl?.showError}
                        errorText={
                            destinationLinkFormControl?.validationMessage
                        }
                        value={destinationLinkFormControl?.value}
                    />
                </ManageTemplateFormItem>
            );
        }

        return (
            <>
                <ManageTemplateFormItem>
                    <Select
                        id={'promotion-page-select'}
                        size="medium"
                        clearable
                        menuPlacement="bottom"
                        label="Promotional page"
                        placeholder="Select a page from the list..."
                        value={promotionsFormControl?.value}
                        onBlur={promotionsFormControl?.handleBlur}
                        onChange={(opt: Option): void => {
                            promotionsFormControl?.setValue(opt);
                        }}
                        options={promotionsOptions}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            mt: 1,
                        }}
                    >
                        <Link
                            variant="caption"
                            onClick={() => {
                                saveContentToCache(false);
                                history.push(
                                    baseUrls.publisher.promotionalPage.add
                                );
                            }}
                        >
                            New page
                        </Link>
                        {promotionsFormControl?.value?.value && (
                            <div>
                                <Link
                                    onClick={() => {
                                        saveContentToCache(false);
                                        history.push(
                                            `${baseUrls.publisher.promotionalPage.list}/edit/${promotionsFormControl?.value?.value}`
                                        );
                                    }}
                                    variant="caption"
                                >
                                    Edit
                                </Link>
                                <Link
                                    onClick={() => {
                                        const promotionPage = pages.find(
                                            (promotionPage) =>
                                                promotionPage._id ===
                                                promotionsFormControl?.value
                                                    ?.value
                                        );
                                        const filteredPromotionalContent =
                                            promotionPage?.promotionalContent
                                                ? promotionPage.promotionalContent.filter(
                                                      (content) =>
                                                          content._id !==
                                                          editItem?._id
                                                  )
                                                : [];
                                        if (filteredPromotionalContent.length) {
                                            showMessage(
                                                'This page is used by another Promotional Content, so it can’t be deleted.',
                                                MessageTypes.error
                                            );
                                            return;
                                        }

                                        setConfirmationDialogOpened(true);
                                    }}
                                    variant="caption"
                                    color="error"
                                    sx={{ ml: 1, cursor: 'pointer' }}
                                >
                                    Delete
                                </Link>
                            </div>
                        )}
                        {promotionsFormControl.showError && (
                            <Typography variant="caption" color="error">
                                {promotionsFormControl.validationMessage}
                            </Typography>
                        )}
                    </Box>
                </ManageTemplateFormItem>
            </>
        );
    };

    return (
        <ContainerBox>
            <Box sx={{ flex: 1, flexDirection: 'column', maxWidth: 1200 }}>
                <ManageTemplate
                    dialogMessage={
                        <>
                            You are about to delete the promotional content{' '}
                            <strong>{nameFormControl?.value}</strong>. This
                            action cannot be undone, so you must be sure and
                            confirm.
                        </>
                    }
                    showDelete={!!editItem}
                    onDelete={(): void => {
                        onDelete(nameFormControl?.value);
                    }}
                    pathArea={'Promotional Content'}
                    returnUrl={returnListUrl}
                    templateInstance={ManageTemplateInstance.PromotionalContent}
                    isNew={isNew}
                    formMessage={formMessage}
                    formBusy={formBusy}
                    onCancel={onCancel}
                    onSubmit={(ev: any): void => {
                        if (
                            destinationLinkSelection ===
                            DestinationLinkSelection.NotSelected
                        ) {
                            ev.preventDefault();
                            setShowDestinationLinkValidationError(true);
                            return;
                        }

                        setShowDestinationLinkValidationError(false);
                        onSubmit(ev, getReqBody());
                    }}
                    onAlertBoxClose={onAlertBoxClose}
                    templateCreateButtonLabel="Save"
                    templateDeleteButtonLabel="Delete"
                >
                    <ManageTemplateFormItem>
                        <TextField
                            id={'name'}
                            fullWidth
                            error={nameFormControl?.showError}
                            errorText={nameFormControl?.validationMessage}
                            type="text"
                            label="Name"
                            autoComplete="off"
                            value={nameFormControl?.value}
                            onBlur={nameFormControl?.handleBlur}
                            onChange={(e: ChangeEvent<any>): void => {
                                const val: string = e?.target?.value;
                                if (val?.length > 50) {
                                    return;
                                }
                                nameFormControl?.setValue(val);
                            }}
                        />
                    </ManageTemplateFormItem>
                    <ManageTemplateFormItem>
                        <TextArea
                            label="Description"
                            required={false}
                            id={'description-textarea'}
                            fullWidth
                            type="text"
                            error={descriptionFormControl?.showError}
                            errorText={
                                descriptionFormControl?.validationMessage
                            }
                            progressBarHelperText={
                                constrainTextArea?.charsMessage
                            }
                            autoComplete="off"
                            rowsMax={4}
                            value={descriptionFormControl?.value}
                            showProgressBar={true}
                            progressBarIndicator={
                                constrainTextArea?.currentChars
                            }
                            progressBarLimit={constrainTextArea?.charsLimit}
                            onBlur={descriptionFormControl?.handleBlur}
                            onChange={(e: ChangeEvent<any>): void => {
                                const val: string = e?.target?.value;
                                if (
                                    val?.length > constrainTextArea?.charsLimit
                                ) {
                                    return;
                                }
                                constrainTextArea?.setCurrentChars(val?.length);
                                descriptionFormControl?.setValue(val);
                            }}
                        />
                    </ManageTemplateFormItem>
                    <ManageTemplateFormItem>
                        <DatePicker
                            label="Promotion expiry date"
                            placeholder="Choose an expiry date in the future"
                            id="expiry-date-time-picker"
                            disablePast={true}
                            onChange={(date: Date | null) => {
                                const formattedDate = date
                                    ? new Date(
                                          date.getFullYear(),
                                          date.getMonth(),
                                          date.getDate(),
                                          23,
                                          59,
                                          59
                                      )
                                    : null;
                                expiryDateFormControl?.setValue(formattedDate);
                            }}
                            value={
                                expiryDateFormControl?.value
                                    ? new Date(expiryDateFormControl?.value)
                                    : null
                            }
                        />
                    </ManageTemplateFormItem>
                    <ManageTemplateFormItem>
                        <TextField
                            id={'article-image'}
                            onChange={(
                                ev: ChangeEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                >
                            ): void =>
                                articleImageFormControl?.setValue(
                                    ev?.target?.value
                                )
                            }
                            onBlur={articleImageFormControl?.handleBlur}
                            label="Article image URL "
                            helperText="The article image can be included in the article content"
                            placeholder="Image file URL"
                            autoComplete="off"
                            error={articleImageFormControl?.showError}
                            errorText={
                                articleImageFormControl?.validationMessage
                            }
                            value={articleImageFormControl?.value}
                        />
                    </ManageTemplateFormItem>
                    <ManageTemplateFormItem>
                        <TextField
                            id={'sidebar-image'}
                            onChange={(
                                ev: ChangeEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                >
                            ): void =>
                                sidebarImageFormControl?.setValue(
                                    ev?.target?.value
                                )
                            }
                            onBlur={sidebarImageFormControl?.handleBlur}
                            label="Sidebar image URL"
                            helperText="The sidebar image is included aside the article content"
                            placeholder="Image file URL"
                            autoComplete="off"
                            error={sidebarImageFormControl?.showError}
                            errorText={
                                sidebarImageFormControl?.validationMessage
                            }
                            value={sidebarImageFormControl?.value}
                        />
                    </ManageTemplateFormItem>
                    <ManageTemplateDialog
                        title="Delete this landing page?"
                        dialogMessage={
                            <>
                                You are about to delete the landing page{' '}
                                <strong>
                                    {promotionsFormControl?.value?.label}
                                </strong>
                                . This action cannot be undone. Do you want to
                                proceed?
                            </>
                        }
                        submitBtnText="Yes, delete"
                        dialogOpenState={confirmationDialogOpened}
                        onDelete={async () => {
                            try {
                                await deletePromotionalPage({
                                    variables: {
                                        item: {
                                            _id: promotionsFormControl?.value
                                                ?.value,
                                            parentContentId:
                                                editItem?._id ?? undefined,
                                        },
                                    },
                                });
                                setConfirmationDialogOpened(false);
                                showMessage(
                                    generateSnackBarFormMessage(
                                        ManageTemplateInstance.PromotionPage,
                                        null,
                                        FormSnackBarType.Deleted
                                    ),
                                    MessageTypes.info
                                );
                                promotionsFormControl?.setValue(null);
                            } catch (ex) {
                                updateFormMessage(ex.message);
                            }
                        }}
                        submitBtnColorType="error"
                        closeDialog={() => setConfirmationDialogOpened(false)}
                    />
                    <ManageTemplateFormItem>
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                display: 'block',
                                fontWeight: 'fontWeightBold',
                                mb: 0.25,
                            }}
                        >
                            Destination link
                        </Typography>
                        <Radio
                            id="external-link-radio-button"
                            checked={
                                destinationLinkSelection ===
                                DestinationLinkSelection.ExternalLink
                            }
                            onChange={() => {
                                setDestinationLinkSelection(
                                    DestinationLinkSelection.ExternalLink
                                );
                                setShowDestinationLinkValidationError(false);
                            }}
                            value="v"
                            label="External link"
                        />
                        <Radio
                            id="custom-landing-page-radio-button"
                            sx={{ ml: 1 }}
                            checked={
                                destinationLinkSelection ===
                                DestinationLinkSelection.CustomLandingPage
                            }
                            onChange={() => {
                                setDestinationLinkSelection(
                                    DestinationLinkSelection.CustomLandingPage
                                );
                                setShowDestinationLinkValidationError(false);
                            }}
                            value="v"
                            label="Custom promotional page"
                        />
                        {showDestinationLinkValidationError && (
                            <Typography variant="caption" color="error">
                                Please choose a destination link
                            </Typography>
                        )}
                    </ManageTemplateFormItem>
                    {destinationLinkSelection !==
                    DestinationLinkSelection.NotSelected
                        ? getDestinationFields()
                        : null}
                </ManageTemplate>
            </Box>
            <Snackbar
                open={!!message}
                message={message ?? ''}
                type={messageType ?? 'error'}
                onClose={clearMessage}
                onUndo={undefined}
            />
        </ContainerBox>
    );
};

export default PromotionalContentAddEdit;
